import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LanguageOptions } from '../../entities/LanguageOptions';
import { Availability } from '../../entities/Availability';
import { apiRequests } from '../../shared/api/apiRequests';
import { toast } from 'react-toastify';
import './Team.css';

const EditTeamStructurePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    id: 0,
    title: '',
    mainLanguageId: 1,
    Availability: {
      AvailableInAllCountries: true,
      AllowedCountriesIds: [],
      DisallowedCountriesIds: [],
    },
    SEOMetatags: {
      Title: '',
      Description: '',
      Keywords: '',
    },
  });

  useEffect(() => {
    loadStructure();
  }, [id]);

  const loadStructure = async () => {
    try {
      setLoading(true);
      const response = await apiRequests.team.getStructure(id);
      setForm({
        id: response.id,
        title: response.title || '',
        mainLanguageId: response.mainLanguageId || 1,
        Availability: {
          AvailableInAllCountries: response.availability?.availableInAllCountries ?? true,
          AllowedCountriesIds: response.availability?.allowedCountriesIds || [],
          DisallowedCountriesIds: response.availability?.disallowedCountriesIds || [],
        },
        SEOMetatags: {
          Title: response.seoMetatags?.title || '',
          Description: response.seoMetatags?.description || '',
          Keywords: response.seoMetatags?.keywords || '',
        },
      });
    } catch {
      toast.error('Ошибка загрузки структуры');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Введите название');
      return;
    }
    setSaving(true);
    try {
      await apiRequests.team.updateTeamStructure(form);
      toast.success('Структура обновлена');
      navigate('/team-structures');
    } catch {
      toast.error('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => navigate('/team-structures');

  if (loading) {
    return <div className="team-loading">Загрузка...</div>;
  }

  return (
    <div className="team-page">
      <div className="team-header">
        <div>
          <button className="team-back" onClick={handleBack}>← Назад</button>
          <h1 className="team-title">Редактирование структуры</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="team-form">
        <div className="team-form-section">
          <h3 className="team-section-title">Основная информация</h3>
          <div className="team-form-row">
            <div className="team-form-field">
              <label>Название <span className="required">*</span></label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="Введите название структуры"
                className="team-input"
              />
            </div>
            <div className="team-form-field" style={{ maxWidth: 200 }}>
              <LanguageOptions
                value={form.mainLanguageId}
                onSelect={(val) => setForm((prev) => ({ ...prev, mainLanguageId: val }))}
              />
            </div>
          </div>
        </div>

        <div className="team-form-section" style={{ borderTop: '1px solid #f0f0f0' }}>
          <Availability
            onSelect={(val) =>
              setForm((prev) => ({
                ...prev,
                Availability: { ...prev.Availability, AvailableInAllCountries: val },
              }))
            }
            onSelectAllowed={(val) =>
              setForm((prev) => ({
                ...prev,
                Availability: { ...prev.Availability, AllowedCountriesIds: val.map(Number) },
              }))
            }
            onSelectDisallowed={(val) =>
              setForm((prev) => ({
                ...prev,
                Availability: { ...prev.Availability, DisallowedCountriesIds: val.map(Number) },
              }))
            }
            initialValues={{
              availableInAllCountries: form.Availability.AvailableInAllCountries,
              allowedCountriesIds: form.Availability.AllowedCountriesIds,
              disallowedCountriesIds: form.Availability.DisallowedCountriesIds,
            }}
          />
        </div>

        <div className="team-form-section" style={{ borderTop: '1px solid #f0f0f0' }}>
          <h3 className="team-section-title">SEO настройки</h3>
          <div className="team-form-row">
            <div className="team-form-field">
              <label>SEO Title</label>
              <input
                type="text"
                value={form.SEOMetatags.Title}
                onChange={(e) => setForm((prev) => ({
                  ...prev,
                  SEOMetatags: { ...prev.SEOMetatags, Title: e.target.value },
                }))}
                placeholder="Заголовок для поисковиков"
                className="team-input"
              />
            </div>
          </div>
          <div className="team-form-row">
            <div className="team-form-field">
              <label>SEO Description</label>
              <textarea
                value={form.SEOMetatags.Description}
                onChange={(e) => setForm((prev) => ({
                  ...prev,
                  SEOMetatags: { ...prev.SEOMetatags, Description: e.target.value },
                }))}
                placeholder="Описание для поисковиков"
                className="team-textarea"
              />
            </div>
          </div>
          <div className="team-form-row">
            <div className="team-form-field">
              <label>SEO Keywords</label>
              <input
                type="text"
                value={form.SEOMetatags.Keywords}
                onChange={(e) => setForm((prev) => ({
                  ...prev,
                  SEOMetatags: { ...prev.SEOMetatags, Keywords: e.target.value },
                }))}
                placeholder="Ключевые слова через запятую"
                className="team-input"
              />
            </div>
          </div>
        </div>

        <div className="team-form-actions">
          <button type="button" className="team-btn-cancel" onClick={handleBack}>
            Отмена
          </button>
          <button type="submit" className="team-btn-submit" disabled={saving}>
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditTeamStructurePage;
